import React, { useState, useContext, useRef, useEffect, Switch } from "react"
import styled from "styled-components"

import { Formik, Form } from "formik"
import Reaptcha from "reaptcha"

import Input from "./Input"
import Select from "./Select"
import AuthContext from "../../context/AuthContext"
import CartContext from "../../context/CartContext"
import SvgIcon from "../../zzz/atoms/icons/svg-icon"
import Lock from "../../zzz/atoms/icons/files/lock.jsx"

import { useQueryParam, StringParam } from "use-query-params"
import { countSubstrings } from "voca"

// ======================
// 	🧱🧱 COMPONENT 🧱🧱
// ======================

const RegisterFormik = ({
  loading,
  setLoading,
  setApiError,
  light = false,
}) => {
  const { performRegister } = useContext(AuthContext)
  const { ensureCurrentCart } = useContext(CartContext)

  const captchaRef = useRef()

  const [captchaToken, setCaptchaToken] = useState(false)
  const [captchaError, setCaptchaError] = useState("")
  const [hidePassword, setHidePassword] = useState(true)

  const [registrationType] = useQueryParam("registrationType")

  const accountTypeInitialValue =
    registrationType == "business" ? "business" : "individual"

  // console.log("RegisterFormik.jsx Loading is: " + loading)

  const clearCaptchaToken = () => {
    setCaptchaToken(false)
  }

  const setApiErrorWrapper = (apiError) => {
    setApiError(apiError)
    if (apiError) {
      captchaRef?.current?.reset?.()
    }
  }

  const submitCallback = async (values) => {
    if (!captchaToken) {
      return setCaptchaError(
        "Please click the Captcha checkbox before continuing."
      )
    } else {
      setCaptchaError("")
    }

    const {
      first_name,
      last_name,
      email,
      password,
      bday,
      opt_in,
      account_type,
      business_registration_number,
      business_name,
      contact_number,
    } = values
    performRegister(
      email,
      password,
      first_name,
      last_name,
      bday,
      !opt_in,
      account_type,
      business_registration_number,
      business_name,
      contact_number,
      captchaToken,
      setLoading,
      setApiErrorWrapper,
      ensureCurrentCart
    )
  }

  return (
    <Formik
      key="register-formik"
      initialValues={{
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        passwordConfirm: "",
        bday: "",
        opt_in: true,
        account_type: accountTypeInitialValue,
        business_registration_number: "",
        business_name: "",
        contact_number: "",
      }}
      validate={validate}
      onSubmit={submitCallback}
    >
      {({ values, errors, touched }) => {
        const prevBdayInputState = usePrevious(values.bday)
        const prevBusinessRegistrationNumber = usePrevious(
          values.business_registration_number
        )
        const prevContactNumberInputState = usePrevious(values.contact_number)

        useEffect(() => {
          // If bday exists and does not match any pattern that needs to be validated and character is added
          if (
            values.bday &&
            !values.bday.match(
              /^[\d]{1}$|^[\d]{2}[/]$|^[\d]{2}[/][\d]{1}$|^[\d]{2}[/][\d]{2}[/]$|^[\d]{2}[/][\d]{2}[/][\d]{1,4}$/gm
            ) &&
            prevBdayInputState.length < values.bday.length
          ) {
            // Remove characters a-z
            values.bday = values.bday.replace(/[a-zA-Z]|[\W]/gm, "")
            // Put in first slash
            if (values.bday.length > 1 && values.bday.substr(2, 1) != "/") {
              values.bday = `${values.bday.substr(0, 2)}/${values.bday.substr(
                2,
                values.bday.length - 2
              )}`
            }
            // Put in second slash
            if (values.bday.length > 4 && values.bday.substr(5, 1) != "/") {
              values.bday = `${values.bday.substr(0, 5)}/${values.bday.substr(
                5,
                values.bday.length - 5
              )}`
            }
            // Trim to length
            if (values.bday.length > 10) values.bday = values.bday.substr(0, 10)
          }
          if (
            values.business_registration_number &&
            !values.business_registration_number.match(
              /^[\d]{4}[/][\d]{6}[/][\d]{2}$/gm
            ) &&
            prevBusinessRegistrationNumber.length <
              values.business_registration_number.length
          ) {
            // Remove characters a-z
            values.business_registration_number =
              values.business_registration_number.replace(/[a-zA-Z]|[\W]/gm, "")
            // Put in first slash
            if (
              values.business_registration_number.length >= 4 &&
              values.business_registration_number.substr(4, 1) != "/"
            ) {
              values.business_registration_number = `${values.business_registration_number.substr(
                0,
                4
              )}/${values.business_registration_number.substr(
                4,
                values.business_registration_number.length - 2
              )}`
            }
            // Put in second slash
            if (
              values.business_registration_number.length >= 11 &&
              values.business_registration_number.substr(11, 1) != "/"
            ) {
              values.business_registration_number = `${values.business_registration_number.substr(
                0,
                11
              )}/${values.business_registration_number.substr(
                11,
                values.business_registration_number.length - 2
              )}`
            }
            // Trim to length
            if (values.business_registration_number.length > 14)
              values.business_registration_number =
                values.business_registration_number.substr(0, 14)
          }
          if (
            values.contact_number &&
            !values.contact_number.match(/^[\d]{10}$/gm) &&
            prevContactNumberInputState.length < values.contact_number.length
          ) {
            // Remove characters a-z
            values.contact_number = values.contact_number.replace(/[\D]/gm, "")

            // Trim to length
            if (values.contact_number.length > 10)
              values.contact_number = values.contact_number.substr(0, 10)
          }
        }, [
          values.bday,
          values.business_registration_number,
          values.contact_number,
        ])

        return (
          <Form key="register-form" name="register-form" id="register-form">
            <Select
              label="Account Type"
              name="account_type"
              id="account_type"
              multiple={false}
              options={[
                { text: "Individual", value: "individual" },
                { text: "Business", value: "business" },
              ]}
              touched={touched}
            />
            {values.account_type == "business" && (
              <Input
                label="Business Name"
                type="text"
                name="business_name"
                id="business_name"
                placeholder=""
                light={light}
                errors={errors}
                touched={touched}
              />
            )}
            {values.account_type == "business" && (
              <Input
                label="Business Registration Number"
                type="text"
                name="business_registration_number"
                id="business_registration_number"
                placeholder="0000/000000/00"
                light={light}
                errors={errors}
                touched={touched}
              />
            )}
            <Input
              label="First Name"
              type="text"
              name="first_name"
              id="first_name"
              autoComplete="given-name"
              placeholder="Jane"
              light={light}
              errors={errors}
              touched={touched}
            />
            <Input
              label="Last Name"
              type="text"
              name="last_name"
              id="last_name"
              autoComplete="family-name"
              placeholder="Doe"
              light={light}
              errors={errors}
              touched={touched}
            />
            <Input
              label="Email"
              type="email"
              name="email"
              id="register-email"
              autoComplete="email"
              placeholder="jane@doe.com"
              light={light}
              errors={errors}
              touched={touched}
            />
            {values.account_type == "business" && (
              <Input
                label="Contact Number"
                type="text"
                name="contact_number"
                id="contact_number"
                placeholder="0117308200"
                light={light}
                errors={errors}
                touched={touched}
              />
            )}
            <Input
              label="Password:"
              type={hidePassword ? "password" : "text"}
              name="password"
              id="register-password"
              autoComplete="new-password"
              toggle={() => {
                setHidePassword((prevState) => !prevState)
              }}
              hidePassword={hidePassword}
              light={light}
              errors={errors}
              touched={touched}
            />
            <Input
              label="Confirm Password:"
              type={hidePassword ? "password" : "text"}
              name="passwordConfirm"
              id="register-passwordConfirm"
              autoComplete="new-password"
              toggle={() => {
                setHidePassword((prevState) => !prevState)
              }}
              hidePassword={hidePassword}
              light={light}
              errors={errors}
              touched={touched}
            />
            {/* <Input
              label="Birthday: (Optional)"
              type="bday"
              name="bday"
              id="register-bday"
              autoComplete="bday"
              placeholder="DD/MM/YYYY"
              light={light}
              errors={errors}
              touched={touched}
            /> */}
            <Input
              label="Opt-in to receive emails *"
              type="checkbox"
              name="opt_in"
              id="register-opt_in"
              light={light}
              errors={errors}
              touched={touched}
            />
            <FinePrint>
              * By completing this form you are signing up to receive our emails
              and can unsubscribe at any time
            </FinePrint>

            <Container>
              <Reaptcha
                sitekey={process.env.GATSBY_GOOGLE_CAPTCHA_SITEKEY}
                onVerify={(responseToken) => {
                  setCaptchaToken(responseToken)
                }}
                onExpire={clearCaptchaToken}
                onError={clearCaptchaToken}
                size="normal"
                ref={captchaRef}
              />
            </Container>
            {captchaError}
            <Button type="submit" disabled={loading}>
              <div>REGISTER</div>
              <SvgIcon SvgComponent={Lock} size="lg" hue="white" shade="000" />
            </Button>
          </Form>
        )
      }}
    </Formik>
  )
}

export default RegisterFormik

// ========
// 	STYLES
// ========
const Container = styled.div`
  .g-recaptcha {
    display: flex;
    justify-content: center;
    margin: 20px 0;
  }
  @media (max-width: ${({ theme }) => theme.breakTiny}) {
    max-width: 270px;
    .g-recaptcha {
      justify-content: flex-start;
    }
  }
`
const FinePrint = styled.div`
  font-size: 12px;
  color: #b8bdc6;
`
const Button = styled.button`
  background: #fd3237;
  border-radius: 3px;
  font-size: 20px;
  color: white;
  border: none;
  display: flex;
  padding: 0;
  overflow: hidden;
  align-items: center;
  cursor: pointer;
  width: 100%;
  justify-content: space-between;
  div {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 15px;
    width: 100%;
  }
  span.icon {
    background: #ed1c24;
    padding: 7px;
  }
`

// ======================
// 	🔧🔧 HELPERS 🔧🔧
// ======================
const validate = ({
  first_name,
  last_name,
  email,
  password,
  passwordConfirm,
  bday,
  account_type,
  business_registration_number,
  business_name,
  contact_number,
}) => {
  const errors = {}
  const addIf = (pred, prop, message) => pred && (errors[prop] = message)

  addIf(
    account_type == "business" && !business_name,
    "business_name",
    "Business name is required"
  )
  addIf(
    account_type == "business" && !business_registration_number,
    "business_registration_number",
    "Business registration number is required"
  )
  addIf(!first_name, "first_name", "First name is required")
  addIf(!last_name, "last_name", "Last name is required")
  addIf(!email, "email", "Email is required")
  addIf(!/\S+@\S+\.\S+/.test(email), "email", "Email is invalid")
  addIf(!password, "password", "Password is required")
  addIf(!passwordConfirm, "passwordConfirm", "Enter your password again")
  addIf(password !== passwordConfirm, "password", "Passwords do not match")
  addIf(
    password.length < 6,
    "password",
    "Password should be at least 6 characters"
  )

  addIf(
    account_type == "business" && !contact_number,
    "contact_number",
    "Contact number is required"
  )
  addIf(
    account_type == "business" && validContactNumber(contact_number),
    "contact_number",
    "Please enter a valid contact number eg. 0117308200"
  )

  addIf(
    validBirthday(bday),
    "bday",
    "Please enter a valid date in the format DD/MM/YYYY"
  )

  addIf(
    validBusinessNumber(business_registration_number),
    "business_registration_number",
    "Please enter a valid business registration number in the format 0000/000000/00"
  )

  return errors
}

function validBirthday(bday) {
  return (
    bday &&
    !/^([1-9]|0[1-9]|[12][0-9]|3[01])[/]([1-9]|0[1-9]|1[012])[0/](19|20)\d\d$/.test(
      bday
    )
  )
}

function validBusinessNumber(bNumber) {
  // Trim number in case its longer then 14 characters since we ignore anything entered after that anyway.
  if (bNumber.length > 14) bNumber = bNumber.substr(0, 14)
  return bNumber && !/^[\d]{4}[/][\d]{6}[/][\d]{2}$/.test(bNumber)
}

function validContactNumber(contactNumber) {
  // Trim number in case its longer then 14 characters since we ignore anything entered after that anyway.
  if (contactNumber.length > 12) contactNumber = contactNumber.substr(0, 12)
  return contactNumber && !/^[\d]{10}$/.test(contactNumber)
}

// Function to use previous value in useEffect
const usePrevious = (value) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}
